import { Spin } from 'antd';
import { ReactElement, useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { REFRIGERATOR_OPTIONS, SortRefrigeratorBy } from '@common/enums';
import { ApplianceModel, ApplianceSource, ApplianceType } from '@common/models';
import { Filters, SectionSubtitle } from '@core/components';
import ConfirmationDialog from '@core/components/confirmation-dialog/ConfirmationDialog';
import { useSelectedMaterial } from '@ee-api/materials-api/useSelectedMaterial';
import { useSelectedStore } from '@ee-api/stores-api/useSelectedStore';
import { DrawingShapeContext } from '@ee-context';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import {
    selectRefrigeratorSelectedOption,
    updateRefrigeratorsFilters,
    updateSelectedRefrigerator,
} from '@redux/slices/refrigeratorSlice';
import { selectZipcode } from '@redux/slices/zipcodeSlice';

import SupplyIcon from '../../../../../../public/assets/icons/supply.svg';
import TrashIcon from '../../../../../../public/assets/icons/trash.svg';
import useAppliance from '../appliances/useAppliance';
import { SortRefrigerator } from './refrigerator-sort';
import { sortRefrigerators } from './refrigerator.utils';
import { RefrigeratorItem } from './refrigeratorItem';
import RefrigeratorOptions from './refrigeratorOptions';
import useFilteredRefrigerators from './useFilteredRefrigerators';
import useRefrigerator from './useRefrigerator';

import './refrigerator.scss';

export function Refrigerator(): ReactElement | null {
    const { t } = useTranslation();

    // FIXME: the sortBy works similar to sinks but has a different enum definition
    // it's necessary to change this so the behavior is consistent
    const [sortBy, setSortBy] = useState<SortRefrigeratorBy | undefined>();
    const [isFilterExpanded, setFilterExpanded] = useState(false);
    const { selectedValue: selectedStore } = useSelectedStore();

    const ref = useRef<HTMLDivElement | null>(null);

    const [isModalVisible, setIsModalVisible] = useState(false);

    const zipcode = useAppSelector(selectZipcode);
    const refrigeratorSelectedOption = useAppSelector(selectRefrigeratorSelectedOption);

    const dispatch = useAppDispatch();

    const selectRefrigeratorOption = (refrigeratorOption: REFRIGERATOR_OPTIONS) => {
        if (refrigeratorSelectedOption === refrigeratorOption) {
            dispatch(updateSelectedRefrigerator(undefined));
            return;
        }

        dispatch(updateSelectedRefrigerator(refrigeratorOption));
        if (refrigeratorOption === REFRIGERATOR_OPTIONS.NO_REFRIGERATORS) {
            if (applianceTypesInDrawingArea([ApplianceType.REFRIGERATOR])) {
                setIsModalVisible(true);
            } else {
                dispatch(updateSelectedRefrigerator(refrigeratorOption));
            }
        } else {
            scrollToLoadedRefrigerators();
        }
    };

    const { refrigeratorFilters, filteredRefrigerators } = useFilteredRefrigerators();
    const { selectedValue: selectedMaterial } = useSelectedMaterial();
    const { areThereShapesInTheDrawingArea, updateActiveDrawingShape, lastActiveDrawingShape } =
        useContext(DrawingShapeContext);

    const { applianceTypesInDrawingArea, deleteAllAppliancesByType } = useAppliance();

    const { isFetchingRefrigerators, isErrorRefrigerators: _error } = useRefrigerator();

    const areRefrigeratorsAvailable = zipcode && selectedStore && areThereShapesInTheDrawingArea && selectedMaterial;
    if (!areRefrigeratorsAvailable) {
        return null;
    }

    // TODO: Fix these functions and deduplicate them to be a generic implementation
    const scrollToLoadedRefrigerators = () => {
        const estimatePageElm = document.getElementById('EstimatePage');
        const drawingArea = document.getElementsByClassName('drawing-area')[0];

        if (!estimatePageElm || !drawingArea || !ref || !ref.current) {
            return;
        }

        estimatePageElm.scrollTo({
            top: estimatePageElm.scrollTop + ref.current.getBoundingClientRect().bottom - drawingArea.clientHeight,
        });
    };

    const onConfirmFromConfirmationModal = () => {
        deleteAllAppliancesByType([ApplianceType.REFRIGERATOR]);
        setIsModalVisible(false);
        updateActiveDrawingShape(lastActiveDrawingShape);
    };

    const onCancelFromConfirmationModal = () => {
        dispatch(updateSelectedRefrigerator(REFRIGERATOR_OPTIONS.BROWSE_REFRIGERATORS));
        setIsModalVisible(false);
    };

    const isProgramRefrigerator = (refrigerator: ApplianceModel) =>
        refrigerator.productSource === ApplianceSource.PROGRAM;
    const isInStoreRefrigerator = (refrigerator: ApplianceModel) =>
        refrigerator.productSource === ApplianceSource.IN_STORE;

    const hasProgramRefrigerators = filteredRefrigerators.some(isProgramRefrigerator);
    const hasStoreRefrigerators = filteredRefrigerators.some(isInStoreRefrigerator);

    const programRefrigerators = filteredRefrigerators.filter(isProgramRefrigerator);
    const inStoreRefrigerators = filteredRefrigerators.filter(isInStoreRefrigerator);

    return (
        <>
            <ConfirmationDialog
                visible={isModalVisible}
                onCancel={onCancelFromConfirmationModal}
                onConfirm={onConfirmFromConfirmationModal}
                icon={
                    refrigeratorSelectedOption === REFRIGERATOR_OPTIONS.SUPPLY_REFRIGERATORS ? (
                        <SupplyIcon />
                    ) : (
                        <TrashIcon />
                    )
                }
                title={
                    refrigeratorSelectedOption === REFRIGERATOR_OPTIONS.SUPPLY_REFRIGERATORS
                        ? t('SCROLLABLE_AREA.REFRIGERATOR.SUPPLY_REFRIGERATOR_TITLE')
                        : t('SCROLLABLE_AREA.REFRIGERATOR.NO_REFRIGERATOR_IN_SPACE_TITLE')
                }
                content={
                    refrigeratorSelectedOption === REFRIGERATOR_OPTIONS.SUPPLY_REFRIGERATORS
                        ? t('SCROLLABLE_AREA.REFRIGERATOR.SUPPLY_REFRIGERATOR_DESCRIPTION')
                        : t('SCROLLABLE_AREA.REFRIGERATOR.NO_REFRIGERATOR_IN_SPACE_DESCRIPTION')
                }
            />
            {filteredRefrigerators.length > 0 && !isFetchingRefrigerators && (
                <div className="refrigerator">
                    <SectionSubtitle
                        name={t('SCROLLABLE_AREA.REFRIGERATOR.TITLE')}
                        description={t('SCROLLABLE_AREA.REFRIGERATOR.DESCRIPTION')}
                        showLine
                    />
                    <>
                        <RefrigeratorOptions
                            onOptionSelect={selectRefrigeratorOption}
                            refrigeratorSelectedOption={refrigeratorSelectedOption}
                            ref={ref}
                        />
                        {refrigeratorSelectedOption === REFRIGERATOR_OPTIONS.BROWSE_REFRIGERATORS ? (
                            <div className="title title-with-sort">
                                {t('SCROLLABLE_AREA.REFRIGERATOR.BROWSE_REFRIGERATOR_INFO')}
                                <SortRefrigerator sortBy={sortBy} setSortBy={setSortBy} />
                            </div>
                        ) : null}
                        {filteredRefrigerators.length > 0 && !isFetchingRefrigerators && (
                            <div className={`refrigerator-card-wrapper ${isFilterExpanded ? 'row-direction' : ''}`}>
                                {refrigeratorSelectedOption === REFRIGERATOR_OPTIONS.BROWSE_REFRIGERATORS && (
                                    <Filters
                                        isFilterExpanded={isFilterExpanded}
                                        setFilterExpanded={setFilterExpanded}
                                        filterOptions={{ ...refrigeratorFilters }}
                                        updateFilters={(input) => dispatch(updateRefrigeratorsFilters(input))}
                                    />
                                )}
                                <div className="refrigerator-group-wrapper">
                                    {refrigeratorSelectedOption === REFRIGERATOR_OPTIONS.BROWSE_REFRIGERATORS ? (
                                        <>
                                            {hasProgramRefrigerators && (
                                                <div className="card-appliance">
                                                    {sortRefrigerators(programRefrigerators, sortBy).map(
                                                        (refrigerator, id) => (
                                                            <RefrigeratorItem key={id} refrigerator={refrigerator} />
                                                        )
                                                    )}
                                                </div>
                                            )}
                                            {hasStoreRefrigerators && (
                                                <div className="card-appliance">
                                                    {sortRefrigerators(inStoreRefrigerators, sortBy).map(
                                                        (refrigerator, id) => (
                                                            <RefrigeratorItem key={id} refrigerator={refrigerator} />
                                                        )
                                                    )}
                                                </div>
                                            )}
                                        </>
                                    ) : null}
                                </div>
                            </div>
                        )}
                        {isFetchingRefrigerators && <Spin style={{ fontSize: 24 }} />}
                    </>
                </div>
            )}
        </>
    );
}
